import React, { useState, useEffect } from "react";
import { AiOutlineFullscreen, AiOutlineFullscreenExit, AiOutlineCloudUpload } from "react-icons/ai";
import { HiBookOpen, HiTerminal } from "react-icons/hi";
import { Button } from "../../../components"; // standard dashboard Button
import { useSelector } from "react-redux";
import { selectUserRole } from "../../../common/authenticateDashboard/authSlice";
import { hasPermission } from "../../../common/rbac/roles";

const useIsDarkTheme = () => {
  const [isDark, setIsDark] = useState(false);
  useEffect(() => {
    const checkTheme = () => {
      const isDarkClass = document.documentElement.classList.contains('dark') ||
        document.body.classList.contains('dark');
      const isDarkMedia = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const bodyBg = window.getComputedStyle(document.body).backgroundColor;
      const isDarkBg = bodyBg ? (
        bodyBg.includes('rgb(26,') ||
        bodyBg.includes('rgb(18,') ||
        bodyBg.includes('rgb(30,') ||
        bodyBg.includes('rgb(16,') ||
        bodyBg.includes('rgb(0,') ||
        bodyBg.includes('rgb(22,') ||
        bodyBg.includes('rgb(23,') ||
        bodyBg.includes('rgb(24,') ||
        bodyBg.includes('rgb(25,') ||
        bodyBg.includes('rgb(27,') ||
        bodyBg.includes('rgb(28,') ||
        bodyBg.includes('rgb(29,')
      ) : false;

      setIsDark(isDarkClass || isDarkMedia || isDarkBg);
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    window.addEventListener('resize', checkTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkTheme);
    };
  }, []);

  return isDark;
};

const HtmlDesignEditor = ({
  htmlDesignCode,
  onChange,
  handleSave,
  nudgeTitle = "Custom HTML Design"
}) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const isDark = useIsDarkTheme();
  
  const userRole = useSelector(selectUserRole);
  const canEdit = hasPermission(userRole, 'EDIT_NUDGES');

  const guidelines = (
    <div className="flex flex-col gap-3">
      <h4 className={`font-semibold text-base flex items-center gap-2 pb-2 border-b ${isDark ? "text-white border-neutral-800" : "text-neutral-900 border-neutral-200"
        }`}>
        <HiBookOpen className={`${isDark ? "text-blue-400" : "text-blue-600"} text-xl shrink-0`} />
        HTML Setup Guidelines
      </h4>
      <div className={`text-xs leading-relaxed flex flex-col gap-2 ${isDark ? "text-neutral-300" : "text-neutral-700"
        }`}>
        <p className={isDark ? "text-neutral-400" : "text-neutral-600"}>To ensure your custom design works seamlessly with website logic, use these class names & placeholders:</p>
        <ul className="space-y-3 mt-1">
          <li className="flex flex-col gap-1">
            <span className={`font-medium ${isDark ? "text-neutral-200" : "text-neutral-800"}`}>1. Coupon Display</span>
            <span className={isDark ? "text-neutral-400" : "text-neutral-600"}>
              Add class <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                }`}>coupon-code</code> or <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                  }`}>cfnudge-coupon</code> to the element containing the discount coupon code text.
            </span>
          </li>
          <li className="flex flex-col gap-1">
            <span className={`font-medium ${isDark ? "text-neutral-200" : "text-neutral-800"}`}>2. Copy Code Trigger</span>
            <span className={isDark ? "text-neutral-400" : "text-neutral-600"}>
              Add class <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                }`}>copy-code</code> or <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                  }`}>cfnudge-copy</code> to the button or element that users click to copy the code.
            </span>
          </li>
          <li className="flex flex-col gap-1">
            <span className={`font-medium ${isDark ? "text-neutral-200" : "text-neutral-800"}`}>3. Close Button</span>
            <span className={isDark ? "text-neutral-400" : "text-neutral-600"}>
              Add class <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                }`}>close-button</code>, <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                  }`}>close-btn</code>, or <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                    }`}>cfnudge_close</code> to the element that dismisses the nudge.
            </span>
          </li>
          <li className="flex flex-col gap-1">
            <span className={`font-medium ${isDark ? "text-neutral-200" : "text-neutral-800"}`}>4. Live Timer/Countdown</span>
            <span className={isDark ? "text-neutral-400" : "text-neutral-600"}>
              Use the placeholder <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                }`}>{"{timer}"}</code> inside your HTML text, or add a span/element with <code className={`font-mono px-1.5 py-0.5 rounded border ${isDark ? "text-blue-400 bg-neutral-950 border-neutral-800" : "text-blue-600 bg-neutral-100 border-neutral-200"
                  }`}>id="timer"</code> to display the live countdown clock.
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
  useEffect(() => {
    if (isFullscreen) {
      document.body.style.setProperty('overflow', 'hidden', 'important');
      document.documentElement.style.setProperty('overflow', 'hidden', 'important');
      
      const fullscreenDiv = document.querySelector('.html-editor-fullscreen');
      const scrollableElements = document.querySelectorAll('div, section, main');
      scrollableElements.forEach(el => {
        if (fullscreenDiv && fullscreenDiv.contains(el)) return;
        const style = window.getComputedStyle(el);
        if (style.overflowY === 'auto' || style.overflowY === 'scroll' || el.classList.contains('overflow-y-auto') || el.classList.contains('overflow-auto')) {
          el.setAttribute('data-prev-overflow-y', el.style.getPropertyValue('overflow-y') || '');
          el.setAttribute('data-prev-overflow', el.style.getPropertyValue('overflow') || '');
          
          el.style.setProperty('overflow-y', 'hidden', 'important');
          el.style.setProperty('overflow', 'hidden', 'important');
          el.scrollTop = 0;
        }
      });
    } else {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      const scrollableElements = document.querySelectorAll('[data-prev-overflow]');
      scrollableElements.forEach(el => {
        const prevY = el.getAttribute('data-prev-overflow-y');
        const prevAll = el.getAttribute('data-prev-overflow');
        if (prevY !== null) {
          if (prevY) el.style.setProperty('overflow-y', prevY);
          else el.style.removeProperty('overflow-y');
          el.removeAttribute('data-prev-overflow-y');
        }
        if (prevAll !== null) {
          if (prevAll) el.style.setProperty('overflow', prevAll);
          else el.style.removeProperty('overflow');
          el.removeAttribute('data-prev-overflow');
        }
      });
    }
    return () => {
      document.body.style.removeProperty('overflow');
      document.documentElement.style.removeProperty('overflow');
      const scrollableElements = document.querySelectorAll('[data-prev-overflow]');
      scrollableElements.forEach(el => {
        const prevY = el.getAttribute('data-prev-overflow-y');
        const prevAll = el.getAttribute('data-prev-overflow');
        if (prevY !== null) {
          if (prevY) el.style.setProperty('overflow-y', prevY);
          else el.style.removeProperty('overflow-y');
          el.removeAttribute('data-prev-overflow-y');
        }
        if (prevAll !== null) {
          if (prevAll) el.style.setProperty('overflow', prevAll);
          else el.style.removeProperty('overflow');
          el.removeAttribute('data-prev-overflow');
        }
      });
    };
  }, [isFullscreen]);

  return (
    <div className="customisableFields w-full lg:w-1/2 p-2 flex flex-col gap-4">
      {/* Guidelines Modal Dialog */}
      {showGuidelines && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-in fade-in duration-200">
          <div className={`w-full max-w-lg rounded-lg p-6 shadow-2xl relative animate-in zoom-in-95 duration-200 border ${isDark ? "bg-[#1c1c1c] border-neutral-800" : "bg-white border-neutral-200"
            }`}>
            <button
              onClick={() => setShowGuidelines(false)}
              className={`absolute top-4 right-4 text-2xl font-bold font-mono transition-colors ${isDark ? "text-neutral-400 hover:text-white" : "text-neutral-500 hover:text-neutral-800"
                }`}
              aria-label="Close guidelines"
            >
              &times;
            </button>

            {guidelines}

            <div className={`flex justify-end mt-6 pt-3 border-t ${isDark ? "border-neutral-800" : "border-neutral-200"
              }`}>
              <button
                onClick={() => setShowGuidelines(false)}
                className="px-4 py-2 rounded bg-blue-600 text-white text-xs font-semibold hover:bg-blue-700 transition-all shadow"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor fullscreen view */}
      {isFullscreen && (
        <div className={`html-editor-fullscreen absolute inset-0 z-50 p-6 flex flex-col gap-4 overflow-hidden ${isDark ? "bg-[#161616]" : "bg-[#f9f9f9]"
          }`}>
          <div className={`flex items-center justify-between border-b pb-3 ${isDark ? "border-neutral-800" : "border-neutral-200"
            }`}>
            <div className="flex items-center gap-2">
              <HiTerminal className="text-blue-500 text-2xl" />
              <div>
                <h3 className={`text-lg font-semibold leading-none ${isDark ? "text-white" : "text-neutral-900"}`}>{nudgeTitle}</h3>
                <span className={`text-xs ${isDark ? "text-neutral-450 text-neutral-400" : "text-neutral-500"}`}>Writing in full screen mode</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowGuidelines(true)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-medium transition-all shadow-sm border ${isDark
                  ? "bg-neutral-900 border-neutral-800 text-neutral-300 hover:text-white hover:bg-neutral-850"
                  : "bg-white border-neutral-300 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50"
                  }`}
                title="Show HTML Setup Guidelines"
              >
                <HiBookOpen className={`text-base shrink-0 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
                Guidelines
              </button>
              <button
                onClick={() => setIsFullscreen(false)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded text-xs font-semibold transition-all shadow-sm border ${isDark
                  ? "bg-neutral-900 border-neutral-800 text-neutral-300 hover:text-white hover:bg-neutral-850"
                  : "bg-white border-neutral-300 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50"
                  }`}
                title="Exit fullscreen"
              >
                <AiOutlineFullscreenExit className="text-base" />
                Minimize
              </button>
            </div>
          </div>
          <textarea
            className={`flex-1 min-h-0 w-full p-4 font-mono text-sm rounded focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/20 resize-none shadow-sm border overflow-y-auto ${isDark
              ? "bg-neutral-900 text-neutral-100 border-neutral-800"
              : "bg-white text-neutral-900 border-neutral-300"
              }`}
            placeholder="<!-- Write your custom HTML structure here -->"
            value={htmlDesignCode || ""}
            onChange={(e) => onChange(e.target.value)}
          />
          <div className="flex items-center justify-between mt-2">
            <p className={`text-xs ${isDark ? "text-neutral-500" : "text-neutral-450"}`}>
              Changes will not persist until you click Save.
            </p>
            {canEdit && (
              <Button
                Icon={AiOutlineCloudUpload}
                active={false}
                onClick={handleSave}
                title="SAVE CHANGES"
              />
            )}
          </div>
        </div>
      )}

      {/* Editor Standard inline view */}
      <div className={`flex items-center justify-between border-b pb-3 ${isDark ? "border-neutral-800" : "border-neutral-200"
        }`}>
        <h3 className={`text-xl font-semibold ${isDark ? "text-white" : "text-neutral-900"}`}>{nudgeTitle}</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowGuidelines(true)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-all shadow-sm border ${isDark
              ? "bg-neutral-900 border-neutral-800 text-neutral-300 hover:text-white hover:bg-neutral-850"
              : "bg-white border-neutral-300 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50"
              }`}
            title="Show HTML Setup Guidelines"
          >
            <HiBookOpen className={`text-base shrink-0 ${isDark ? "text-blue-400" : "text-blue-600"}`} />
            Guidelines
          </button>
          <button
            onClick={() => setIsFullscreen(true)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded text-xs font-medium transition-all shadow-sm border ${isDark
              ? "bg-neutral-900 border-neutral-800 text-neutral-300 hover:text-white hover:bg-neutral-850"
              : "bg-white border-neutral-300 text-neutral-700 hover:text-neutral-900 hover:bg-neutral-50"
              }`}
            title="Maximize Editor"
          >
            <AiOutlineFullscreen className="text-base" />
            Fullscreen
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <label className={`text-sm font-medium ${isDark ? "text-neutral-300" : "text-neutral-700"}`}>Enter Nudge HTML Code:</label>
        <textarea
          rows={12}
          className={`w-full p-3 font-mono text-sm rounded focus:outline-none focus:border-blue-500 resize-y shadow-sm border ${isDark
            ? "bg-neutral-950 text-neutral-100 border-neutral-800"
            : "bg-white text-neutral-900 border-neutral-300"
            }`}
          placeholder="<!-- Write your custom HTML structure here -->"
          value={htmlDesignCode || ""}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>

      <div className="buttons flex mt-4 items-center justify-center">
        {canEdit && (
          <Button
            Icon={AiOutlineCloudUpload}
            active={false}
            onClick={handleSave}
            title="SAVE"
          />
        )}
      </div>
    </div>
  );
};

export default HtmlDesignEditor;
